import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Button, Dropdown, Space, Avatar, Drawer } from 'antd';
import api from '../utils/api';
import { useIsMobile } from '../hooks/useIsMobile';
import {
  DatabaseOutlined,
  UserOutlined,
  ShopOutlined,
  SettingOutlined,
  ImportOutlined,
  TagOutlined,
  TeamOutlined,
  ThunderboltOutlined,
  FileTextOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  BarChartOutlined,
  CalendarOutlined,
  AccountBookOutlined,
  AppstoreOutlined,
  SafetyOutlined,
  FolderOutlined,
  KeyOutlined,
  LinkOutlined,
  RocketOutlined,
  OrderedListOutlined,
  PlusCircleOutlined,
  StarOutlined,
  DollarOutlined,
  LineChartOutlined,
  FieldTimeOutlined
} from '@ant-design/icons';

const { Header, Sider, Content } = Layout;

// 全部菜单定义
const ALL_MENU_ITEMS = [
  {
    key: '/orders',
    icon: <DatabaseOutlined />,
    label: '订单管理'
  },
  {
    key: '/dashboard',
    icon: <BarChartOutlined />,
    label: '数据看板'
  },
  {
    key: '/daily-dashboard',
    icon: <CalendarOutlined />,
    label: '每日看板'
  },
  {
    key: 'settlement',
    icon: <AccountBookOutlined />,
    label: '结算汇总',
    children: [
      { key: '/settlement/supplier', label: '按主体结算' },
      { key: '/settlement/overall', label: '整体结算' },
      { key: '/settlement/profit', label: '利润表' },
      { key: '/settlement/bill-invoice', label: '账单发票' }
    ]
  },
  {
    key: '/orders/import',
    icon: <ImportOutlined />,
    label: '批量导入'
  },
  {
    key: 'permission',
    icon: <SafetyOutlined />,
    label: '权限管理',
    children: [
      { key: '/suppliers', label: '主体管理' },
      { key: '/users', label: '用户管理' },
      { key: '/permission-templates', label: '权限关联' },
      { key: '/api-strategies', label: 'API策略配置' }
    ]
  },
  {
    key: 'api-order',
    icon: <RocketOutlined />,
    label: '自动下单',
    children: [
      { key: '/api-orders/new', label: 'API下单' },
      { key: '/api-orders', label: '对接列表' }
    ]
  },
  {
    key: 'category',
    icon: <FolderOutlined />,
    label: '分类管理',
    children: [
      { key: '/statuses', label: '状态管理' },
      { key: '/teams', label: '小组管理' },
      { key: '/platforms', label: '平台管理' }
    ]
  },
  {
    key: '/growth-methods',
    icon: <ThunderboltOutlined />,
    label: '涨粉方式'
  },
  {
    key: '/supplier-prices',
    icon: <DollarOutlined />,
    label: '参考价格管理'
  },
  {
    key: '/fan-monitor',
    icon: <LineChartOutlined />,
    label: '涨粉监控'
  },
  {
    key: '/scheduled-tasks',
    icon: <FieldTimeOutlined />,
    label: '定时任务'
  },
  {
    key: '/operation-logs',
    icon: <FileTextOutlined />,
    label: '操作日志'
  },
  {
    key: '/yfx',
    icon: <StarOutlined />,
    label: '有效粉'
  },
  {
    key: 'api',
    icon: <LinkOutlined />,
    label: '接口管理',
    children: [
      { key: '/api-config', label: '接口配置' },
      { key: '/api-call-logs', label: '调用记录' }
    ]
  },
  {
    key: 'api-open',
    icon: <LinkOutlined />,
    label: 'API开放平台',
    children: [
      { key: '/api-open/endpoints', label: '端点管理' },
      { key: '/api-open/keys', label: 'Key管理' },
      { key: '/api-open/logs', label: '调用日志' }
    ]
  }
];

function MainLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [collapsed, setCollapsed] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [version, setVersion] = useState('');

  useEffect(() => {
    fetch('/VERSION')
      .then(r => r.text())
      .then(v => setVersion(v.trim() || 'v1.0.0'))
      .catch(() => setVersion('v1.0.0'));
  }, []);

  // 从服务器刷新用户权限数据
  const refreshUser = useCallback(async () => {
    try {
      const res = await api.get('/auth/me');
      if (res.code === 200 && res.data) {
        localStorage.setItem('user', JSON.stringify(res.data));
        setUser(res.data);
        // 通知子组件用户数据已更新
        window.dispatchEvent(new Event('userUpdated'));
      }
    } catch (err) {
      // token 过期等，跳转登录
      if (err?.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
      }
    }
  }, [navigate]);

  useEffect(() => {
    const stored = localStorage.getItem('user');
    if (stored) {
      setUser(JSON.parse(stored));
    }
    // 页面获得焦点时刷新用户权限（管理员修改权限后即时生效）
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        refreshUser();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleVisibility);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleVisibility);
    };
  }, [refreshUser]);

  const isSuperAdmin = user?.username === 'admin';
  const isAdmin = user?.role === 'admin';
  const menuPermissions = user?.menu_permissions || [];

  // 根据 menu_permissions 过滤菜单（超管看全部）
  const isMenuVisible = (key) => {
    if (isSuperAdmin) return true;
    const perm = menuPermissions.find(p => p.key === key);
    return perm ? perm.is_visible === 1 : false;
  };

  const filterMenuItems = (items) => {
    return items
      .filter(item => {
        if (item.children) {
          const visibleChildren = item.children.filter(c => isMenuVisible(c.key));
          return visibleChildren.length > 0;
        }
        return isMenuVisible(item.key);
      })
      .map(item => {
        if (item.children) {
          return {
            ...item,
            children: item.children.filter(c => isMenuVisible(c.key))
          };
        }
        return item;
      });
  };

  const menuItems = filterMenuItems(ALL_MENU_ITEMS);

  const userMenuItems = [
    {
      key: 'profile',
      icon: <SettingOutlined />,
      label: '个人设置',
      onClick: () => navigate('/profile')
    },
    {
      type: 'divider'
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      danger: true,
      onClick: () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
      }
    }
  ];

  const siderContent = (
    <>
      <div style={{
        height: 64,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        fontSize: (collapsed && !isMobile) ? 14 : 16,
        fontWeight: 'bold',
        whiteSpace: 'nowrap'
      }}>
        {(collapsed && !isMobile) ? '涨粉' : '自媒体涨粉管理'}
      </div>
      <Menu
        theme="dark"
        mode="inline"
        selectedKeys={[location.pathname]}
        items={menuItems}
        onClick={({ key }) => { navigate(key); if (isMobile) setDrawerOpen(false); }}
      />
      {!isMobile && (
        <div style={{
          position: 'absolute',
          bottom: 16,
          width: '100%',
          textAlign: 'center',
          color: 'rgba(255,255,255,0.35)',
          fontSize: 12
        }}>
          {collapsed ? 'v' + (version || '1.0.0').replace('v','').split('.')[0] : version || 'v1.0.0'}
        </div>
      )}
    </>
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {isMobile ? (
        <Drawer
          placement="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          width={220}
          bodyStyle={{ padding: 0, background: '#001529' }}
          headerStyle={{ display: 'none' }}
        >
          {siderContent}
        </Drawer>
      ) : (
        <Sider trigger={null} collapsible collapsed={collapsed} theme="dark" style={{ position: 'relative' }}>
          {siderContent}
        </Sider>
      )}
      <Layout>
        <Header style={{
          padding: isMobile ? '0 12px' : '0 24px',
          background: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 1px 4px rgba(0,0,0,.08)',
          position: 'sticky',
          top: 0,
          zIndex: 10
        }}>
          <Button
            type="text"
            icon={isMobile ? <MenuUnfoldOutlined /> : (collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />)}
            onClick={() => isMobile ? setDrawerOpen(true) : setCollapsed(!collapsed)}
          />
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
            <Space style={{ cursor: 'pointer' }}>
              <Avatar icon={<UserOutlined />} size={isMobile ? 'small' : 'default'} />
              {!isMobile && <span>{user?.name || user?.username}</span>}
              {!isMobile && isAdmin && <span style={{ color: '#1677ff', fontSize: 12 }}>[管理员]</span>}
            </Space>
          </Dropdown>
        </Header>
        <Content style={{
          margin: isMobile ? 8 : 24,
          padding: isMobile ? 12 : 24,
          background: '#fff',
          borderRadius: 8,
          minHeight: 280
        }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}

export default MainLayout;
